import type { CurrencyBaseUnit } from '@shared/models/units/currency-units.model';
import { ProjectResultMetrics } from './ProjectResultMetrics';
import { useProjectListMaxGFA } from '@client/store/project';
import { useProjectListMapSelectedProjectId } from '../../../project-list-map.context';
import { getProjectDonutRadii } from '../../../helpers/project-program-bar.helpers';
import React, {
  MouseEventHandler,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { ProgramDonutChart } from '@features/design/ProgramDonutChart';
import { getActivityTypeLabel } from '@shared/helpers/design/activities.helpers';
import {
  getProgramDonutSegmentTooltipPlacement,
  getProgramDonutTooltipSlotProps,
} from '@features/design/helpers/program-donut-tooltip.helpers';
import { IProjectInfo } from '@shared/models/project.model';
import {
  ActivityMix,
  ActivityType,
  activityTypeColorRecord,
  programActivityTypes,
} from '@shared/models/design/activities/activity-type.model';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { Icon } from '@ui/Icon';
import { GhostButton } from '@ui/GhostButton';
import {
  PROJECT_MAP_CARD_HEIGHT,
  PROJECT_MAP_CARD_GAP,
  PROJECT_SELECTED_BACKGROUND,
  PROJECT_HOVER_BACKGROUND,
  PROJECT_SELECTED_SHADOW,
  PROJECT_SURFACE_RADIUS,
} from '../../project-list.constants';
import ProjectListItemBarCharts from './ProjectListItemBarCharts';

const DONUT_CHART_SIZE = 111;
const DONUT_CENTER_RADIUS = 28;
const DONUT_INNER_RADIUS = (90 * 18) / 100;
const DONUT_MAX_OUTER_RADIUS = ((90 * 46) / 100) * 1.5 * 0.75 * 0.8;

interface ProjectMapCardProps {
  project: IProjectInfo;
  name: ReactNode;
  owner: ReactNode;
  labels: ReactNode;
  hideResultMetrics?: boolean;
  co2: string;
  co2Diff?: number;
  cost: string;
  currency?: CurrencyBaseUnit;
  onClick: MouseEventHandler;
  onContextMenu: MouseEventHandler;
  onMenuClick: MouseEventHandler;
  onOpen: MouseEventHandler;
  menuOpen: boolean;
}

export const ProjectMapCard = ({
  project,
  name,
  owner,
  labels,
  hideResultMetrics = false,
  co2,
  co2Diff,
  cost,
  currency,
  onClick,
  onContextMenu,
  onMenuClick,
  onOpen,
  menuOpen,
}: ProjectMapCardProps) => {
  const selected = useProjectListMapSelectedProjectId() === project.id;
  const gfa = project.gfa ?? 0;
  const [segmentHovered, setSegmentHovered] = useState(false);
  const [donutChartElement, setDonutChartElement] =
    useState<HTMLElement | null>(null);
  const maxGfa = useProjectListMaxGFA();
  const { innerRadius, outerRadius } = getProjectDonutRadii(
    gfa,
    maxGfa,
    DONUT_INNER_RADIUS,
    DONUT_MAX_OUTER_RADIUS,
    DONUT_CENTER_RADIUS,
  );
  const programs: ActivityMix = {};
  for (const { type, area } of project.program_areas ?? []) {
    programs[type] = (programs[type] ?? 0) + area;
  }
  const assignedArea = Object.values(programs).reduce(
    (sum, area) => sum + area,
    0,
  );
  programs[ActivityType.Other] =
    (programs[ActivityType.Other] ?? 0) + Math.max(0, gfa - assignedArea);
  const legend = programActivityTypes
    .filter((type) => (programs[type] ?? 0) > 0)
    .sort((a, b) => (programs[b] ?? 0) - (programs[a] ?? 0));
  const programRowRef = useRef<HTMLDivElement>(null);
  const programMeasurementRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);
  const [hidePercentages, setHidePercentages] = useState(false);
  const [truncatedProgram, setTruncatedProgram] = useState<ActivityType>();
  const programMeasurementKey = legend
    .map((type) => `${type}:${programs[type]}`)
    .join(',');
  useLayoutEffect(() => {
    const row = programRowRef.current;
    const measurement = programMeasurementRef.current;
    const labelContainer = labelsRef.current;
    if (!row || !measurement || !labelContainer) return;
    const measure = () => {
      const rowStyle = getComputedStyle(row);
      const counterWidth =
        parseFloat(
          getComputedStyle(labelContainer).getPropertyValue(
            '--project-label-counter-width',
          ),
        ) || 0;
      const available =
        row.clientWidth -
        (parseFloat(rowStyle.paddingRight) || 0) -
        counterWidth -
        (counterWidth ? 8 : 0);
      setHidePercentages(measurement.getBoundingClientRect().width > available);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(measurement);
    observer.observe(labelContainer);
    return () => observer.disconnect();
  }, [programMeasurementKey, gfa]);

  return (
    <Box
      sx={{
        height: PROJECT_MAP_CARD_HEIGHT + PROJECT_MAP_CARD_GAP,
        paddingBottom: `${PROJECT_MAP_CARD_GAP}px`,
        containerType: 'inline-size',
      }}
    >
      <Box
        role="treeitem"
        aria-label={project.name}
        aria-selected={selected}
        data-project-map-card=""
        onClick={onClick}
        onContextMenu={onContextMenu}
        sx={{
          '--project-card-text-boost': selected ? 0.2 : 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          padding: '12px 16px 4px 4px',
          backgroundColor: selected
            ? PROJECT_SELECTED_BACKGROUND
            : menuOpen
              ? PROJECT_HOVER_BACKGROUND
              : 'transparent',
          boxShadow: selected ? PROJECT_SELECTED_SHADOW : 'none',
          '&:hover': {
            backgroundColor: selected
              ? PROJECT_SELECTED_BACKGROUND
              : PROJECT_HOVER_BACKGROUND,
          },
          border: '1px solid',
          borderColor: 'transparent',
          borderRadius: PROJECT_SURFACE_RADIUS,
          cursor: 'pointer',
          position: 'relative',
          '@container (max-width: 400px)': {
            padding: '8px 8px 8px 4px',
            gap: 0,
            '& .project-card-results': {
              display: 'grid',
              gridTemplateColumns: '18px 1fr 1fr',
              gap: '4px 6px',
            },
            '& .project-card-bar': { gridColumn: '2 / 4' },
            '& .project-card-carbon, & .project-card-cost': { width: 'auto' },
            '& .project-card-carbon': { gridColumn: 2 },
            '& .project-card-cost': { gridColumn: 3 },
          },
          '@container (max-width: 350px)': {
            '& .project-card-results': { gridTemplateColumns: '18px 1fr' },
            '& .project-card-bar': { gridColumn: 2 },
            '& .project-card-cost': { gridColumn: 2 },
          },
          '&:hover .project-card-actions, &:focus-within .project-card-actions':
            {
              opacity: 1,
              visibility: 'visible',
            },
          '&:hover .project-card-labels, &:focus-within .project-card-labels': {
            paddingRight: '104px',
          },
        }}
      >
        <Box
          className="project-card-actions"
          sx={{
            position: 'absolute',
            zIndex: 1,
            top: 8,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            opacity: selected || menuOpen ? 1 : 0,
            visibility: selected || menuOpen ? 'visible' : 'hidden',
          }}
        >
          <IconButton
            className="project-card-menu"
            aria-label={`Project menu: ${project.name}`}
            onClick={onMenuClick}
            sx={{ width: 18, height: 18, padding: 0 }}
          >
            <Icon category="mui" icon="MoreHoriz" size={16} />
          </IconButton>
          <GhostButton
            variant={selected ? 'primary' : 'default'}
            size="large"
            aria-label={`Open ${project.name}`}
            onClick={onOpen}
          >
            Open
          </GhostButton>
        </Box>
        <Tooltip
          title={
            segmentHovered
              ? ''
              : `Gross floor area: ${getDisplayNumber(gfa)} m²`
          }
          placement={getProgramDonutSegmentTooltipPlacement(true)}
          enterDelay={200}
          enterNextDelay={200}
          slotProps={{
            ...getProgramDonutTooltipSlotProps(donutChartElement, {
              disableFlip: true,
              outerRadiusRatio: outerRadius / DONUT_CHART_SIZE,
            }),
            transition: { timeout: 0 },
          }}
        >
          <Box
            className="project-card-donut"
            sx={{
              width: DONUT_CHART_SIZE,
              // Match the chart box so GFA and segment tooltips share one anchor.
              height: DONUT_CHART_SIZE,
              position: 'relative',
              // Cancel the card's asymmetric vertical padding.
              top: -4,
              '@container (max-width: 400px)': { top: 0 },
              flexShrink: 0,
              textAlign: 'center',
            }}
          >
            <Box
              ref={setDonutChartElement}
              className="project-card-donut-chart"
              sx={{ width: '100%', height: '100%' }}
            >
              <ProgramDonutChart
                programs={programs}
                onSegmentHoverChange={setSegmentHovered}
                tooltipAnchorEl={donutChartElement}
                totalGfa={gfa}
                nia={0}
                compact
                innerRadius={(innerRadius / DONUT_CHART_SIZE) * 100}
                outerRadius={(outerRadius / DONUT_CHART_SIZE) * 100}
              />
            </Box>
            <Box
              data-project-card-area=""
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                // 2 px below center matches the documented Map-card GFA label.
                transform: 'translate(-50%, calc(-50% + 2px))',
                display: 'grid',
                gridTemplateRows: '10px 10px',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                color: selected ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)',
              }}
            >
              <Box
                component="span"
                sx={{ fontSize: 10, lineHeight: '10px', fontWeight: 500 }}
              >
                {getDisplayNumber(gfa, {
                  notation: gfa < 10_000 ? 'standard' : 'compact',
                })}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  lineHeight: '10px',
                  transform: 'translate(1px, 1px)',
                  color: selected ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.4)',
                }}
              >
                m²
              </Box>
            </Box>
          </Box>
        </Tooltip>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            position: 'relative',
            top: 0,
          }}
        >
          <Box
            className="project-card-labels"
            ref={programRowRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
              overflow: 'hidden',
              paddingRight: selected || menuOpen ? '104px' : 0,
            }}
          >
            <Box
              ref={programMeasurementRef}
              aria-hidden="true"
              sx={{
                position: 'absolute',
                visibility: 'hidden',
                pointerEvents: 'none',
                width: 'max-content',
                display: 'flex',
                gap: '18px',
                fontSize: 12,
                whiteSpace: 'nowrap',
              }}
            >
              {legend.map((type) => (
                <Box key={type} sx={{ display: 'flex', gap: '7px' }}>
                  <Box sx={{ width: 7 }} />
                  <span>{getActivityTypeLabel(type)}</span>
                  <span>
                    {Math.round(((programs[type] ?? 0) / gfa) * 100)}%
                  </span>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flex: '0 1 auto',
                minWidth: 0,
                gap: '18px',
                overflow: 'hidden',
                minHeight: 18,
                whiteSpace: 'nowrap',
              }}
            >
              {legend.map((type) => (
                <Tooltip
                  key={type}
                  title={
                    hidePercentages || truncatedProgram === type
                      ? `${getActivityTypeLabel(type)} ${Math.round(((programs[type] ?? 0) / gfa) * 100)}%`
                      : undefined
                  }
                >
                  <Box
                    onMouseEnter={(event) => {
                      const name =
                        event.currentTarget.querySelector<HTMLElement>(
                          '[data-program-name]',
                        );
                      setTruncatedProgram(
                        name && name.scrollWidth > name.clientWidth
                          ? type
                          : undefined,
                      );
                    }}
                    onMouseLeave={() => setTruncatedProgram(undefined)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px',
                      fontSize: 12,
                      flex: '1 1 auto',
                      minWidth: 0,
                      color: selected ? 'rgba(0,0,0,1)' : 'rgba(0,0,0,0.8)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        flexShrink: 0,
                        borderRadius: '2px',
                        backgroundColor: activityTypeColorRecord[type],
                        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                      }}
                    />
                    <Box
                      component="span"
                      data-program-name={type}
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {getActivityTypeLabel(type)}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: hidePercentages ? 'none' : 'inline',
                        flexShrink: 0,
                        color: selected ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
                      }}
                    >
                      {Math.round(((programs[type] ?? 0) / gfa) * 100)}%
                    </Box>
                  </Box>
                </Tooltip>
              ))}
            </Box>
            <Box
              ref={labelsRef}
              sx={{
                display: 'flex',
                flex: hidePercentages ? '0 0 auto' : '1 1 0',
                ...(hidePercentages && {
                  width: 'var(--project-label-counter-width, 0px)',
                }),
                minWidth: 'var(--project-label-counter-width, 0px)',
              }}
            >
              {labels}
            </Box>
          </Box>
          <Box sx={{ maxWidth: '100%', height: 26, marginTop: '-4px' }}>
            {name}
          </Box>
          <Box
            className="project-card-results"
            sx={{
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minWidth: 0,
            }}
          >
            <Box
              onClick={(event) => event.stopPropagation()}
              sx={{
                width: 18,
                height: 18,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(selected && {
                  '& .MuiAvatar-root': { color: 'rgba(255,255,255,1)' },
                }),
              }}
            >
              {owner}
            </Box>
            <Box className="project-card-bar" sx={{ flex: 1, minWidth: 32 }}>
              <ProjectListItemBarCharts
                projectId={project.id}
                gfa={gfa}
                results={project.results}
                baselineResults={project.baseline_results}
                compact
              />
            </Box>
            {!hideResultMetrics && (
              <ProjectResultMetrics
                co2={co2}
                co2Diff={co2Diff}
                cost={cost}
                currency={currency}
                selected={selected}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
